import { msalConfig } from '@/authConfig';
import { InteractionType } from '@azure/msal-browser';
import type { AccountInfo } from '@azure/msal-common';
import { MsalAuthenticationTemplate, useMsal } from '@azure/msal-react';
import { type PropsWithChildren, type ReactElement, useEffect } from 'react';

const loginRequest = {
  scopes: ['openid', 'profile', 'offline_access'],
  authority: msalConfig.auth.authority,
};

function isValidAccount(account: AccountInfo | null): account is AccountInfo {
  return (
    account !== null && account.idTokenClaims?.aud === msalConfig.auth.clientId
  );
}

/**
 * Ensures the active account is set to a valid account for this application.
 */
function useEnsureValidActiveAccount(): void {
  const { instance } = useMsal();

  useEffect(() => {
    const activeAccount = instance.getActiveAccount();

    if (isValidAccount(activeAccount)) {
      return;
    }

    const accounts = instance.getAllAccounts();
    const validAccount = accounts.find(isValidAccount);

    if (validAccount) {
      instance.setActiveAccount(validAccount);
    }
  }, [instance]);
}

type AuthenticationGateProps = PropsWithChildren<{
  loginComponent: React.ElementType;
}>;

export const AuthenticationGate = ({
  children,
  loginComponent: LoginComponent,
}: AuthenticationGateProps): ReactElement => {
  useEnsureValidActiveAccount();

  // Wrapper components that ignore MSAL-specific props
  const LoadingWrapper = (): ReactElement => <LoginComponent />;
  const ErrorWrapper = (): ReactElement => <LoginComponent />;

  return (
    <MsalAuthenticationTemplate
      interactionType={InteractionType.Redirect}
      authenticationRequest={loginRequest}
      loadingComponent={LoadingWrapper}
      errorComponent={ErrorWrapper}
    >
      {children}
    </MsalAuthenticationTemplate>
  );
};
